import {
  CardMedia,
  IconButton,
  Link,
  Typography,
} from '@one-thd/sui-atomic-components';
import { ArrowDown, ArrowUp } from '@one-thd/sui-icons';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import React from 'react';
import { IAlert, ITenders } from '../../../models/common';
import ILineItem from '../../../models/line-item';
import { IOrderDetailsResponse } from '../../../models/order-details';
import { IOrder, IOrderParams } from '../../../models/purchase-history';
import { TSelectedRow } from '../../../models/purchase-history/table';
import { routeBuilder } from '../../../utils/general';
import { buildName } from '../../../utils/order-history/PHTable';
import { putAccountIdValueInSessionStorage } from '../../../utils/shared/accountIdValueStorage';
import { InfoAndGuidesPopover } from '../../order-details/InfoAndGuidesPopover/InfoAndGuidesPopover';
import { orderNumberFormat } from '../../shared/order-number-formatter/OrderNumberFormatter';
import { generateOrderOrigin, sliceLongText } from '../util/tableUtils';
import {
  PoJobNameButton,
  ProductImages,
  ProjectNameCell,
  TablePayments,
} from './index';

// TODO: Replace Luxon's DateTime.local().toLocaleString(DateTime.DATE_HUGE) with Day.js equivalent
// const formattedDate = dayjs().format('MMMM D, YYYY');

export const PHTableData = (
  allLineItemImages: ILineItem[],
  formatOrderOrigin: (text: string) => React.JSX.Element,
  handleRowClick: (id: number) => void,
  index: number,
  order: ILineItem | IOrder,
  orderDetailsLoaded: boolean,
  orderParams: IOrderParams,
  searchByObj: {
    [key: string]: string;
  },
  searchEnabled: boolean,
  selected: TSelectedRow,
  setAlertState: (value: IAlert | null) => void,
  tableHeaderState: Array<{
    enabled: boolean;
  }>,
  orderDetails: IOrderDetailsResponse | null,
  orderDetailsError: AxiosError | null,
) => {
  const lineItemData = Object.prototype.hasOwnProperty.call(
    searchByObj,
    'itemDescription',
  );

  const orderDetailsLink = `/myaccount/order-details${routeBuilder(
    orderParams,
  )}`;

  const orderDetailsOrderNumber =
    new URLSearchParams(orderDetailsLink.split('?')[1]).get('orderNumber') ??
    '';

  const handleOrderDetailsLinkClick = () => {
    putAccountIdValueInSessionStorage(
      orderDetailsOrderNumber,
      (order as IOrder)?.accountIdValue,
      (order as IOrder)?.accountIdType,
    );
  };

  const tenderModifier = (tenders: ITenders[]) => {
    const newTenders: ITenders[] = [];
    tenders?.forEach((tender) => {
      if (Number(tender?.amount) < 0) {
        const modifiedTender = {
          ...tender,
          amount: tender?.amount?.toString().replace('-', ''),
        };
        newTenders.push(modifiedTender);
        return;
      }
      newTenders.push(tender);
    });
    return newTenders;
  };

  if (lineItemData) {
    // Line Item Data
    return [
      {
        content: '',
        showTableData: true,
      },
      {
        content: (
          <Typography variant="body-base">
            {(order as ILineItem)?.salesDate &&
              DateTime.fromISO((order as ILineItem)?.salesDate ?? '').toFormat(
                'LL/dd/yy',
              )}
          </Typography>
        ),
        align: '',
        style: { width: '129px' },
        verticalAlign: '',
        showTableData: true,
      },
      {
        content: (
          <ProductImages
            isLoading={searchEnabled ? false : orderDetailsLoaded === false}
            lineItems={searchEnabled ? [order] : allLineItemImages}
            maxShow={4}
            variant="medium"
            hideCanceled={false}
          />
        ),
        style: { width: '78px' },
        showTableData: true,
      },
      {
        content: (
          <div className="sui-flex sui-flex-col sui-gap-1 sui-my-[10px] sui-mr-3">
            <Link
              variant="body-base"
              href={orderDetailsLink}
              onClick={handleOrderDetailsLinkClick}
            >
              {sliceLongText(`${(order as ILineItem)?.description ?? ''}`, 28)}
            </Link>
            <Typography variant="body-base" color="subtle">
              SKU {(order as ILineItem)?.skuNumber ?? ''}
            </Typography>
            {((order as ILineItem)?.documents?.length ?? 0) > 0 && (
              <InfoAndGuidesPopover
                documents={(order as ILineItem)?.documents}
                internetSKU={(order as ILineItem)?.omsId}
                modelNumber={(order as ILineItem)?.modelNumber}
                storeSKU={
                  (order as ILineItem)?.storeSKU ||
                  (order as ILineItem)?.skuNumber
                }
              />
            )}
          </div>
        ),
        style: { width: '145px' },
        showTableData: true,
      },
      {
        content: (
          <Link
            variant="body-base"
            href={orderDetailsLink}
            onClick={handleOrderDetailsLinkClick}
          >
            {(order as ILineItem)?.orderNumber ?? ''}
          </Link>
        ),
        style: { width: '129px' },
        showTableData: true,
      },
      {
        content: (
          <Typography variant="body-base">
            {(Boolean((order as ILineItem)?.transactionId) &&
              Boolean((order as ILineItem)?.registerNumber) &&
              `${(order as ILineItem)?.registerNumber?.toString()}-${
                (order as ILineItem)?.transactionId
              }`) ??
              ''}
          </Typography>
        ),
        style: { width: '129px' },
        showTableData: true,
      },
      {
        content: (
          <Typography variant="body-base">
            {(order as ILineItem)?.quantity ?? ''}
          </Typography>
        ),
        style: { width: '115px' },
        showTableData: true,
      },
      {
        content: (
          <Typography variant="body-base">
            $
            {(order as ILineItem)?.originalUnitPrice?.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) ?? ''}
          </Typography>
        ),
        style: { width: '94px' },
        showTableData: true,
      },
      {
        content: (
          <Typography variant="body-base">
            $
            {(order as ILineItem)?.otherDiscountAmount?.toLocaleString(
              'en-US',
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            ) ?? ''}
          </Typography>
        ),
        style: { width: '101px' },
        showTableData: true,
      },
      {
        content: (
          <Typography variant="body-base">
            $
            {(order as ILineItem)?.netUnitPrice?.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) ?? ''}
          </Typography>
        ),
        style: { width: '105px' },
        showTableData: true,
      },
    ];
  }

  // Table Data
  return [
    {
      content: searchEnabled ? (
        <div />
      ) : (
        <>
          <IconButton
            onClick={() => handleRowClick(index)}
            icon={selected[index] === index ? ArrowUp : ArrowDown}
          />
        </>
      ),
      verticalAlign: 'middle',
      align: 'center',
      showTableData: true,
    },
    {
      content:
        Object.keys(searchByObj)?.length === 0 ||
        Object.keys(searchByObj)?.[0] === 'itemDescription' ? (
          <ProductImages
            isLoading={orderDetailsLoaded === false}
            lineItems={allLineItemImages ?? []}
            maxShow={3}
            variant="small"
          />
        ) : (
          <div
            style={{
              width: '32px',
              height: '32px',
            }}
          >
            <CardMedia
              src="https://assets.thdstatic.com/images/v1/no-img.svg"
              alt="Unavailable image"
            />
          </div>
        ),
      showTableData: tableHeaderState?.[0]?.enabled,
    },
    {
      content: (
        <Link
          variant="body-base"
          href={orderDetailsLink}
          onClick={handleOrderDetailsLinkClick}
        >
          {orderNumberFormat(
            (order as IOrder)?.orderNumber ??
              (order as IOrder)?.orderNumbers?.[0],
            (order as IOrder)?.transactionIdWithCheckDigit ??
              (order as IOrder)?.transactionId,
            (order as IOrder)?.registerNumber,
            false,
          ).toUpperCase()}
        </Link>
      ),
      showTableData: tableHeaderState?.[1]?.enabled,
    },
    {
      content: (
        <Typography variant="body-base">
          {(order as IOrder)?.salesDate &&
            DateTime.fromISO((order as IOrder)?.salesDate ?? '').toFormat(
              'LL/dd/yy',
            )}
        </Typography>
      ),
      showTableData: tableHeaderState?.[2]?.enabled,
    },
    {
      content: formatOrderOrigin(
        generateOrderOrigin(order as IOrder | ILineItem) ?? '',
      ),
      showTableData: tableHeaderState?.[3]?.enabled,
    },
    {
      content: (
        <ProjectNameCell order={order as IOrder} orderDetails={orderDetails} />
      ),
      showTableData: tableHeaderState?.[4]?.enabled ?? true,
    },
    {
      content: <PoJobNameButton order={order} setAlertState={setAlertState} />,
      showTableData: tableHeaderState?.[5]?.enabled,
      style: { width: '124px' },
    },
    {
      content: (
        <TablePayments
          orderNumber={
            (order as IOrder)?.orderNumber ??
            (order as IOrder)?.orderNumbers?.[0]
          }
          receiptDetails={(order as IOrder)?.receiptDetails}
          tenders={tenderModifier((order as IOrder)?.tenders ?? [])}
          orderDetailsLoaded={orderDetailsLoaded}
          paymentMethods={
            (orderDetails as IOrderDetailsResponse)?.paymentMethods
          }
          orderDetailsError={orderDetailsError}
        />
      ),
      showTableData: tableHeaderState?.[6]?.enabled,
    },
    {
      content: (
        <Typography
          variant="body-base"
          style={{
            overflow: 'hidden',
            width: '103px',
          }}
        >
          {sliceLongText(
            buildName(
              (order as IOrder)?.purchaserFirstName ?? '',
              (order as IOrder)?.purchaserLastName ?? '',
              (order as IOrder)?.buyerName,
              (order as IOrder)?.buyerId,
            ),
            28,
          )}
        </Typography>
      ),
      showTableData: tableHeaderState?.[7]?.enabled,
    },
    {
      content: (
        <Typography
          variant="body-base"
          style={{
            overflow: 'hidden',
            width: '103px',
          }}
        >
          {sliceLongText((order as IOrder)?.nickName ?? '', 28)}
        </Typography>
      ),
      showTableData: tableHeaderState?.[8]?.enabled,
    },
    {
      content: (
        <Typography variant="body-base">
          {orderDetails?.statusDescription === 'Returned' ? '-' : ''}$
          {(
            (order as IOrder)?.returnTotal ??
            (order as IOrder)?.totalAmount ??
            (order as ILineItem)?.originalUnitPrice
          )?.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) ?? '---'}
        </Typography>
      ),
      style: { width: '92px' },
      showTableData: tableHeaderState?.[9]?.enabled,
    },
  ];
};
